import apiClient, { publicApiClient } from './client';
import { resolveApiBaseUrl } from './baseUrl';

export type LinkImportStatus =
  | 'queued'
  | 'resolving'
  | 'downloading'
  | 'extracting'
  | 'uploading'
  | 'transcribing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type LinkImportProvider = 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'x';

export interface LinkImportPreview {
  preview_id?: string;
  url: string;
  normalized_url: string;
  provider: LinkImportProvider;
  title?: string;
  thumbnail_url?: string;
  thumbnail_proxy_url?: string;
  duration: number;
  estimated_minutes: number;
  importable: boolean;
  metadata_status?: 'pending' | 'ready' | 'failed';
}

export interface LinkImport {
  id: string;
  file_id?: string;
  transcription_id?: string;
  url: string;
  normalized_url: string;
  provider: LinkImportProvider;
  title?: string;
  thumbnail_url?: string;
  duration: number;
  status: LinkImportStatus;
  progress: number;
  error_code?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLinkImportRequest {
  url: string;
  language?: string;
  enable_diarization?: boolean;
  max_speakers?: number;
}

export async function previewLinkImport(url: string): Promise<LinkImportPreview> {
  const response = await apiClient.post<LinkImportPreview>('/link-imports/preview', { url });
  return response.data;
}

export async function previewPublicLinkImport(url: string): Promise<LinkImportPreview> {
  const response = await publicApiClient.post<LinkImportPreview>('/public/link-imports/preview', { url });
  return response.data;
}

export async function createLinkImport(data: CreateLinkImportRequest): Promise<LinkImport> {
  const response = await apiClient.post<LinkImport>('/link-imports', data);
  return response.data;
}

export async function getLinkImport(id: string): Promise<LinkImport> {
  const response = await apiClient.get<LinkImport>(`/link-imports/${id}`);
  return response.data;
}

export async function cancelLinkImport(id: string): Promise<LinkImport> {
  const response = await apiClient.post<LinkImport>(`/link-imports/${id}/cancel`);
  return response.data;
}

export function resolveLinkImportThumbnailUrl(preview: Pick<LinkImportPreview, 'thumbnail_proxy_url' | 'thumbnail_url'>): string {
  const thumbnailUrl = preview.thumbnail_proxy_url || preview.thumbnail_url || '';
  if (!thumbnailUrl) return '';
  if (/^https?:\/\//i.test(thumbnailUrl)) return thumbnailUrl;
  if (thumbnailUrl.startsWith('/')) {
    return `${new URL(resolveApiBaseUrl()).origin}${thumbnailUrl}`;
  }
  return thumbnailUrl;
}
